import { useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { LoadingOverlay } from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { ProviderEntity } from '../../../models/provider';
import { CredentialsEntity } from '../../../models/credentials';
import {
  createThirdPartyRedirectMachine as createThirdPartyRedirectMachineImport,
  ThirdPartyRedirectMachineProps,
} from '../../machines/thirdPartyRedirectMachine';
import { getAutoRedirectMobileQueryParamAnalytic as getAutoRedirectMobileQueryParamAnalyticImport } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/getAutoRedirectMobileQueryParamAnalytic';
import { ApplicationEvent } from '../../../models/analytics';
import { useApplicationEvent } from '../../../core/Analytics/useApplicationEvent';
import { OpenThirdPartyRedirectScreen } from './OpenThirdPartyRedirectScreen';
import { ThirdPartyRedirectPendingScreen } from './ThirdPartyRedirectPendingScreen';
import {
  getNavigationStrategy,
  getRedirectUrl,
  navigateToThirdParty as navigateToThirdPartyImport,
  shouldAutoRedirectToThirdParty,
} from './thirdPartyRedirectHelpers';
import { ThirdPartyRedirectInstructionsScreen } from './ThirdPartyRedirectInstructionsScreen';

export type ThirdPartyRedirectMachineViewProps = {
  initialCredentials: ThirdPartyRedirectMachineProps['initialCredentials'];
  selectedProvider: ProviderEntity;
  clientData: { isClientVerified: boolean; clientName: string };
  onDone: (credentials: CredentialsEntity) => void;
  onError: (credentials: CredentialsEntity) => void;
  onBack: () => void;
  navigateToThirdParty?: typeof navigateToThirdPartyImport;
  createThirdPartyRedirectMachine?: typeof createThirdPartyRedirectMachineImport;
  getAutoRedirectMobileQueryParamAnalytic?: typeof getAutoRedirectMobileQueryParamAnalyticImport;
};

export const ThirdPartyRedirectMachineView = ({
  initialCredentials,
  selectedProvider,
  clientData,
  onDone,
  onError,
  onBack,
  navigateToThirdParty = navigateToThirdPartyImport,
  createThirdPartyRedirectMachine = createThirdPartyRedirectMachineImport,
  getAutoRedirectMobileQueryParamAnalytic = getAutoRedirectMobileQueryParamAnalyticImport,
}: ThirdPartyRedirectMachineViewProps) => {
  const thirdPartyRedirectMachine = useMemo(
    () =>
      createThirdPartyRedirectMachine({
        initialCredentials,
      }),
    [initialCredentials, createThirdPartyRedirectMachine]
  );
  const [current, send] = useMachine(thirdPartyRedirectMachine);
  const currentCredentials = current.context.credentials;
  const { supplementalInformation } = currentCredentials;
  const { data: configuration } = useConfiguration();
  const { isClientVerified, clientName } = clientData;
  const { isDesktop } = userAgent;
  const redirectUrl = getRedirectUrl({ supplementalInformation });

  const { analytics } = useApplicationEvent({
    applicationEvent: ApplicationEvent.THIRD_PARTY_REDIRECT_MACHINE,
  });

  const openRedirect = () => {
    if (!redirectUrl) {
      throw new ApplicationError({
        errorStatus: ErrorStatus.INTERNAL_ERROR,
        errorReason: ErrorReason.UNEXPECTED_ERROR,
      });
    }

    send('EVENT_GO_TO_REDIRECT_PENDING');

    navigateToThirdParty({
      redirectUrl,
      navigationStrategy: getNavigationStrategy(),
    });
  };

  const handleGoBack = () => {
    send('EVENT_GO_BACK');
  };

  useEffect(() => {
    const autoRedirectMobileEvent = getAutoRedirectMobileQueryParamAnalytic();

    if (autoRedirectMobileEvent) analytics.sendInteractionEventOnce(autoRedirectMobileEvent);
  }, [getAutoRedirectMobileQueryParamAnalytic, analytics]);

  useEffect(() => {
    if (current.matches('processAutoRedirect')) {
      const shouldAutoRedirect = shouldAutoRedirectToThirdParty({
        isDesktop,
        navigationStrategy: getNavigationStrategy(),
        configuration,
        redirectUrl,
      });

      if (shouldAutoRedirect) {
        openRedirect();
      }
    }

    if (current.matches('done')) {
      onDone(currentCredentials);
    }

    if (current.matches('error')) {
      onError(currentCredentials);

      const { error } = current.context;

      if (error) {
        throw error;
      }
    }

    if (current.matches('processGoBack')) {
      onBack();
    }
  });

  if (current.matches('uiPollingOpenRedirect')) {
    if (!isDesktop) {
      return (
        <OpenThirdPartyRedirectScreen
          redirectUrl={redirectUrl}
          provider={selectedProvider}
          isVerified={isClientVerified}
          onButtonClick={openRedirect}
          onBack={handleGoBack}
        />
      );
    }

    return (
      <ThirdPartyRedirectInstructionsScreen
        provider={selectedProvider}
        clientName={clientName}
        isVerified={isClientVerified}
        onClick={openRedirect}
        onBack={handleGoBack}
      />
    );
  }

  if (current.matches('uiPollingRedirectPending')) {
    return (
      <ThirdPartyRedirectPendingScreen
        credentials={currentCredentials}
        providerName={selectedProvider.displayName}
        onBack={handleGoBack}
      />
    );
  }

  return <LoadingOverlay />;
};
